import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Sparkles, MessageSquare, Plus, Trash2, Lock, ArrowRight, X } from 'lucide-react';
import AskEnfernoChat from '../components/AskEnfernoChat';
import { useAuth } from '../context/AuthContext';
import { useTierState } from '../hooks/useTierState';
import { useFeatureFlag } from '../context/FeatureFlagsContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AI_CONSENT_REMINDER_KEY = 'enferno_ai_consent_shown_v1';

const CoachPaywall = () => (
  <div className="bg-transparent text-enf-text-primary min-h-screen pt-20 pb-16">
    <div className="max-w-3xl mx-auto px-6">
      <Link to="/dashboard" className="inline-flex items-center gap-2 text-enf-text-secondary hover:text-enf-text-primary text-sm mb-8 transition-colors">
        <ArrowRight className="w-4 h-4 rotate-180" /> Back to dashboard
      </Link>
      <div className="bg-enf-surface-deep border border-enf-surface-border rounded-xl p-8 md:p-12 text-center" data-testid="coach-paywall">
        <div className="w-14 h-14 mx-auto mb-5 rounded-full bg-enf-accent/10 border border-enf-accent-fg/30 flex items-center justify-center">
          <Lock className="w-6 h-6 text-enf-accent-fg" />
        </div>
        <div className="text-xs uppercase tracking-[0.3em] font-semibold text-enf-accent-fg mb-2">Enferno feature</div>
        <h1 className="font-display text-3xl md:text-4xl tracking-wide leading-tight mb-3">Unlock AI Coach</h1>
        <p className="text-enf-text-secondary text-sm leading-relaxed max-w-md mx-auto mb-6">
          Personalized workout generation and the Coach chat (100 messages / month) are Enferno features. Claude reads your profile and adapts to you, session by session.
        </p>
        <Link
          to="/pricing"
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-enf-accent hover:bg-enf-accent-hover rounded-md text-sm font-semibold transition-colors"
          data-testid="coach-paywall-cta"
        >
          See pricing <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </div>
  </div>
);

const ConsentReminderModal = ({ onDismiss }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60" data-testid="ai-consent-reminder">
    <div className="bg-enf-surface-deep border border-enf-surface-border rounded-xl p-6 max-w-sm w-full shadow-xl">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-enf-accent-fg" />
          <div className="text-xs font-semibold uppercase tracking-widest text-enf-text-secondary">AI Data Notice</div>
        </div>
        <button type="button" onClick={onDismiss} className="text-enf-text-muted hover:text-enf-text-primary transition-colors" aria-label="Dismiss">
          <X className="w-4 h-4" />
        </button>
      </div>
      <h3 className="font-display text-2xl tracking-wide mb-2">Your profile powers the AI</h3>
      <p className="text-sm text-enf-text-secondary leading-relaxed mb-5">
        Enferno AI uses your training profile — experience, goals, equipment, injury flags, and pain logs — to personalize recommendations. You can review or change this in{' '}
        <Link to="/profile" className="text-enf-role-info hover:underline" onClick={onDismiss}>
          Profile → Your Data &amp; Consents
        </Link>.
      </p>
      <button
        type="button"
        onClick={onDismiss}
        className="w-full px-4 py-2.5 bg-enf-accent hover:bg-enf-accent-hover rounded-md text-sm font-semibold transition-colors"
        data-testid="ai-consent-got-it"
      >
        Got it
      </button>
    </div>
  </div>
);

const CoachApp = () => {
  const { user } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [activeSessionId, setActiveSessionId] = useState(null);
  const [showConsentReminder, setShowConsentReminder] = useState(false);

  useEffect(() => {
    if (!user) return;
    try {
      if (!localStorage.getItem(AI_CONSENT_REMINDER_KEY)) setShowConsentReminder(true);
    } catch { /* ignore */ }
  }, [user]);

  const dismissConsentReminder = () => {
    setShowConsentReminder(false);
    try { localStorage.setItem(AI_CONSENT_REMINDER_KEY, '1'); } catch { /* ignore */ }
  };

  const loadSessions = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/ai/chat/sessions`);
      setSessions(data.sessions || []);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { if (user) loadSessions(); }, [user, loadSessions]);

  const deleteSession = async (id) => {
    if (!window.confirm('Delete this conversation?')) return;
    try {
      await axios.delete(`${API}/ai/chat/sessions/${id}`);
      if (activeSessionId === id) setActiveSessionId(null);
      await loadSessions();
    } catch { /* ignore */ }
  };

  if (!user) {
    return (
      <div className="bg-transparent text-enf-text-primary min-h-screen pt-20 pb-16">
        <div className="max-w-2xl mx-auto px-6 text-center py-24">
          <Sparkles className="w-8 h-8 text-enf-accent-fg mx-auto mb-3" />
          <h1 className="font-display text-5xl tracking-wide leading-none mb-3">Sign in to chat with Enferno.</h1>
          <p className="text-enf-text-secondary">Your AI coach is just a sign-in away.</p>
        </div>
      </div>
    );
  }

  return (
    <>
    <div className="bg-transparent text-enf-text-primary min-h-screen pt-20 pb-16">
      <div className="max-w-7xl mx-auto px-6">
        <div className="mb-8">
          <div className="text-enf-role-info text-xs uppercase tracking-[0.3em] font-semibold mb-2 flex items-center gap-2">
            <Sparkles className="w-3.5 h-3.5" /> AI training coach
          </div>
          <h1 className="font-display text-6xl md:text-7xl tracking-wide leading-none">Ask Enferno.</h1>
          <p className="text-enf-text-secondary mt-3 text-sm max-w-2xl">
            Your AI training coach. Knows your profile, your injuries, and your recent training. Bring real questions — get straight answers.
          </p>
        </div>

        <div className="grid lg:grid-cols-[280px_1fr] gap-6">
          {/* Session history sidebar */}
          <div className="space-y-2">
            <button
              onClick={() => setActiveSessionId(null)}
              className="w-full px-3 py-2.5 bg-enf-accent hover:bg-enf-accent-hover rounded-md text-sm font-semibold flex items-center justify-center gap-2 transition-colors"
              data-testid="coach-new-conversation"
            >
              <Plus className="w-4 h-4" /> New conversation
            </button>

            <div className="text-[10px] uppercase tracking-widest text-enf-text-muted font-semibold px-2 pt-3">
              History ({sessions.length})
            </div>

            <div className="space-y-1 max-h-[60vh] overflow-y-auto">
              {sessions.length === 0 && (
                <div className="text-xs text-enf-text-muted px-2 py-3">No conversations yet.</div>
              )}
              {sessions.map((s) => (
                <div key={s.id} className={`group flex items-center gap-1 rounded-md transition-colors ${
                  activeSessionId === s.id ? 'bg-enf-accent/10 border border-enf-accent-fg/40' : 'border border-transparent hover:bg-enf-surface-deep'
                }`}>
                  <button
                    onClick={() => setActiveSessionId(s.id)}
                    className="flex-1 text-left px-2.5 py-2 min-w-0"
                    data-testid={`coach-session-${s.id}`}
                  >
                    <div className="flex items-center gap-2 mb-0.5">
                      <MessageSquare className="w-3 h-3 text-enf-text-muted flex-shrink-0" />
                      <div className="text-xs text-enf-text-secondary truncate">{s.preview || 'New conversation'}</div>
                    </div>
                    <div className="text-[10px] text-enf-text-muted ml-5">
                      {s.message_count} msgs · {new Date(s.last_message_at || s.created_at).toLocaleDateString()}
                    </div>
                  </button>
                  <button
                    onClick={() => deleteSession(s.id)}
                    className="opacity-0 group-hover:opacity-100 text-enf-text-muted hover:text-enf-role-warning p-2 transition-opacity"
                    aria-label="Delete conversation"
                    data-testid={`coach-delete-${s.id}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Chat */}
          <AskEnfernoChat
            variant="page"
            sessionId={activeSessionId}
            onSessionChange={(id) => {
              setActiveSessionId(id);
              loadSessions();
            }}
          />
        </div>
      </div>
    </div>

    {showConsentReminder && <ConsentReminderModal onDismiss={dismissConsentReminder} />}
    </>
  );
};

const ComingSoonCoach = () => (
  <div className="bg-transparent text-enf-text-primary min-h-screen pt-20 pb-16">
    <div className="max-w-2xl mx-auto px-6 text-center py-24" data-testid="coach-coming-soon">
      <Sparkles className="w-10 h-10 text-enf-accent-fg mx-auto mb-4" />
      <div className="text-xs uppercase tracking-[0.3em] font-semibold text-enf-accent-fg mb-2">Coming soon</div>
      <h1 className="font-display text-4xl md:text-5xl tracking-wide leading-tight mb-4">AI Coach is on the way.</h1>
      <p className="text-enf-text-secondary text-sm leading-relaxed max-w-md mx-auto mb-6">
        We're polishing the Ask Enferno coach for launch. Subscribe now to get full access the moment it's live.
      </p>
      <Link
        to="/roadmap"
        className="inline-flex items-center gap-2 px-5 py-2.5 bg-enf-accent hover:bg-enf-accent-hover rounded-md text-sm font-semibold transition-colors"
        data-testid="coach-coming-soon-cta"
      >
        See the roadmap <ArrowRight className="w-4 h-4" />
      </Link>
    </div>
  </div>
);

const Coach = () => {
  const { user, loading } = useAuth();
  const { is_paid: isPaid, loading: tierLoading } = useTierState();
  const { enabled: coachEnabled, loading: flagsLoading } = useFeatureFlag('ai_coach');
  if (loading || tierLoading || flagsLoading) return null;
  if (!user) return null;
  if (!coachEnabled) return <ComingSoonCoach />;
  if (!isPaid) return <CoachPaywall />;
  return <CoachApp />;
};

export default Coach;
